import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Send, Loader2 } from 'lucide-react'

interface ReplyComposerProps {
  onSend: (text: string) => Promise<void>
  disabled?: boolean
  draftText?: string | null
  /** Changes when a newer draft replaces the old one (a decision's notification). */
  draftId?: string | null
}

export function ReplyComposer({ onSend, disabled, draftText, draftId }: ReplyComposerProps) {
  const [text, setText] = useState(draftText || '')
  const [sending, setSending] = useState(false)

  // The draft usually arrives after this box has mounted, because the ticket's messages load
  // separately. `useState(draftText)` only reads its argument once, so the box stayed empty and
  // the reviewer had to notice a small "Load agent draft" link. Fill it in when the draft turns
  // up, once, and never over something the reviewer has already typed.
  //
  // A newer draft (the notification drafted when a decision is made) replaces the old one in the
  // box, unless the reviewer has edited what was there.
  const filledFrom = useRef<{ id: string | null | undefined; text: string } | null>(
    draftText ? { id: draftId, text: draftText } : null
  )
  useEffect(() => {
    if (!draftText) return
    const previous = filledFrom.current
    if (previous && previous.id === draftId) return
    filledFrom.current = { id: draftId, text: draftText }
    setText((current) => (!current.trim() || (previous && current === previous.text) ? draftText : current))
  }, [draftText, draftId])

  const handleSend = async () => {
    if (!text.trim() || sending) return
    setSending(true)
    try {
      await onSend(text.trim())
      setText('')
    } finally {
      setSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Reply to Customer</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {draftText && text !== draftText && (
          <div className="text-xs text-muted-foreground">
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => setText(draftText)}
            >
              Load agent draft →
            </button>
          </div>
        )}
        <Textarea
          placeholder="Type your reply..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          // Tall enough to read a whole drafted reply before sending it. At five rows a reviewer
          // saw the greeting and two lines, and had to scroll inside the box to check the rest.
          rows={text.trim() ? 14 : 5}
          disabled={disabled || sending}
          className="resize-y min-h-[100px]"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            ⌘+Enter to send
          </span>
          <Button
            onClick={handleSend}
            disabled={!text.trim() || disabled || sending}
            size="sm"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Send Reply
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
